import { useState } from 'react';
import { ArrowDown, ArrowDownToLine, ArrowUp, Edit2, History, Package, RotateCcw, ShoppingCart, X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import type { InventoryItem } from '@/hooks/useInventory';
import type { InsightItem } from '@/hooks/useInventoryInsights';
import { useItemMovements, useItemPurchases } from '@/hooks/useItemHistory';
import { formatReais } from '@/lib/utils';
import { StockStatusBadge, GiroBadge, POStatusBadge } from './StatusBadge';
import { useEscapeKey } from '@/hooks/useEscapeKey';
import { useFocusTrap } from '@/hooks/useFocusTrap';
import { EmptyState, ErrorState } from '@mustauto/ui/web';
import { STOCK_UNIT_LABELS, formatStockNumber, formatStockQuantity, normalizeStockUnit } from '@mustauto/utils';
import { useCan } from '@/hooks/useCan';

interface ItemDrawerProps {
  item: InventoryItem;
  insight?: InsightItem;
  onClose: () => void;
  onEdit: (item: InventoryItem) => void;
  onMovement: (item: InventoryItem) => void;
  onCreatePO: (item: InventoryItem) => void;
}

export function ItemDrawer({ item, insight, onClose, onEdit, onMovement, onCreatePO }: ItemDrawerProps) {
  useEscapeKey(onClose);
  const can = useCan();
  const trapRef = useFocusTrap<HTMLDivElement>();
  const [tab, setTab] = useState<'info' | 'movements' | 'purchases'>('info');
  const movementsQuery = useItemMovements(tab === 'movements' ? item.id : null);
  const purchasesQuery = useItemPurchases(tab === 'purchases' ? item.id : null);

  const margin = item.salePrice > 0 && item.unitCost != null && item.unitCost > 0
    ? ((item.salePrice - item.unitCost) / item.salePrice) * 100
    : 0;

  return (
    <div ref={trapRef} role="dialog" aria-modal="true" className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} aria-hidden="true" />
      <div className="relative z-10 w-full max-w-[480px] bg-background shadow-xl overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between border-b bg-background p-4">
          <div className="flex items-center gap-3 min-w-0">
            <h2 className="text-lg font-semibold truncate">{item.name}</h2>
            <StockStatusBadge quantity={item.quantity} minQuantity={item.minQuantity} />
          </div>
          <button onClick={onClose} aria-label="Fechar" className="rounded-md p-1 hover:bg-muted transition-colors shrink-0">
            <X size={18} />
          </button>
        </div>

        {/* Actions */}
        <div className="flex gap-2 border-b p-4">
          {can('estoque.cadastrar') && <button
            onClick={() => onEdit(item)}
            className="flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-medium hover:bg-muted transition-colors"
          >
            <Edit2 size={12} /> Editar
          </button>}
          {can('estoque.movimentar') && <button
            onClick={() => onMovement(item)}
            className="flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-medium text-emerald-700 hover:bg-emerald-50 dark:text-emerald-300 dark:hover:bg-emerald-950/40 transition-colors"
          >
            <ArrowDownToLine size={12} /> Movimentar estoque
          </button>}
          {can('compras.pedir') && <button
            onClick={() => onCreatePO(item)}
            className="flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-medium text-blue-700 hover:bg-blue-50 dark:text-blue-300 dark:hover:bg-blue-950/40 transition-colors"
          >
            <ShoppingCart size={12} /> Criar OC
          </button>}
        </div>

        {/* Tabs */}
        <div className="flex border-b">
          {(['info', 'movements', 'purchases'] as const).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`flex-1 px-4 py-2.5 text-sm font-medium transition-colors ${
                tab === t
                  ? 'border-b-2 border-primary text-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {t === 'info' ? 'Informações' : t === 'movements' ? 'Movimentações' : 'Compras'}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          {tab === 'info' && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <InfoField label="SKU" value={item.sku ?? '—'} />
                <InfoField label="Part Number" value={item.partNumber ?? '—'} />
                <InfoField label="Categoria" value={item.category} />
                <InfoField label="Unidade" value={STOCK_UNIT_LABELS[normalizeStockUnit(item.unit) ?? 'unidade']} />
                <InfoField label="Localização" value={item.location ?? '—'} />
                <InfoField label="Fornecedor" value={item.supplier ?? '—'} />
              </div>

              <div className="rounded-lg border p-4 space-y-3">
                <h3 className="text-sm font-semibold">Estoque</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <div className="text-xs text-muted-foreground">Quantidade</div>
                    <div className={`text-xl font-bold ${item.quantity <= 0 ? 'text-red-600 dark:text-red-400' : item.quantity <= item.minQuantity ? 'text-amber-600 dark:text-amber-400' : ''}`}>
                      {formatStockQuantity(item.quantity, item.unit)}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground">Mínimo</div>
                    <div className="text-xl font-bold">{formatStockQuantity(item.minQuantity, item.unit)}</div>
                  </div>
                </div>
              </div>

              <div className="rounded-lg border p-4 space-y-3">
                <h3 className="text-sm font-semibold">Preços</h3>
                <div className="grid grid-cols-2 gap-3">
                  {can('estoque.custo') && <InfoField label="Custo unitário" value={formatReais(item.unitCost)} />}
                  <InfoField label="Preço de venda" value={formatReais(item.salePrice)} />
                  {can('estoque.custo') && <InfoField label="Custo médio" value={item.avgCost ? formatReais(item.avgCost) : '—'} />}
                  {can('estoque.custo') && <InfoField label="Última compra" value={item.lastPurchasePrice ? formatReais(item.lastPurchasePrice) : '—'} />}
                </div>
                {can('estoque.custo') && <div className="flex items-center gap-2 pt-1">
                  <span className="text-xs text-muted-foreground">Margem:</span>
                  <span className={`text-sm font-bold ${margin >= 30 ? 'text-emerald-600 dark:text-emerald-400' : margin >= 15 ? 'text-amber-600 dark:text-amber-400' : 'text-red-600 dark:text-red-400'}`}>
                    {margin.toFixed(1)}%
                  </span>
                </div>}
              </div>

              {insight && (
                <div className="rounded-lg border p-4 space-y-3">
                  <h3 className="text-sm font-semibold">Giro</h3>
                  <div className="flex items-center gap-3">
                    <GiroBadge label={insight.giroLabel} />
                    <span className="text-sm text-muted-foreground">
                      {insight.giro30d} saídas nos últimos 30 dias
                    </span>
                  </div>
                  {insight.vezesZerou > 0 && (
                    <p className="text-xs text-amber-600 dark:text-amber-400">
                      Zerou {insight.vezesZerou}x nos últimos 60 dias
                    </p>
                  )}
                </div>
              )}

              {item.compatibleMakes && item.compatibleMakes.length > 0 && (
                <div className="rounded-lg border p-4 space-y-2">
                  <h3 className="text-sm font-semibold">Compatibilidade</h3>
                  <div className="flex flex-wrap gap-1">
                    {item.compatibleMakes.map((m) => (
                      <Badge key={m} variant="secondary">{m}</Badge>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          {tab === 'movements' && (
            movementsQuery.isLoading ? (
              <div className="flex justify-center py-8">
                <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
              </div>
            ) : movementsQuery.isError && (movementsQuery.data?.movements ?? []).length === 0 ? (
              <ErrorState
                title="Não foi possível carregar as movimentações"
                error={movementsQuery.error}
                onRetry={() => movementsQuery.refetch()}
              />
            ) : (movementsQuery.data?.movements ?? []).length === 0 ? (
              <EmptyState
                compact
                icon={<History size={22} />}
                title="Nenhuma movimentação"
                description="Entradas, saídas e ajustes deste item aparecem aqui."
              />
            ) : (
              <div className="space-y-1">
                {(movementsQuery.data?.movements ?? []).map((m) => (
                  <div key={m.id} className="flex items-start gap-3 rounded-lg border px-3 py-2">
                    <div className="mt-0.5">
                      {m.type === 'in' ? (
                        <ArrowDown size={14} className="text-emerald-600 dark:text-emerald-400" />
                      ) : m.type === 'out' ? (
                        <ArrowUp size={14} className="text-red-500 dark:text-red-400" />
                      ) : m.type === 'purchase_receipt' ? (
                        <Package size={14} className="text-blue-600 dark:text-blue-400" />
                      ) : (
                        <RotateCcw size={14} className="text-amber-500 dark:text-amber-400" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">
                          {m.type === 'in' ? 'Entrada' : m.type === 'out' ? 'Saída' : m.type === 'purchase_receipt' ? 'Recebimento OC' : 'Ajuste'}
                        </span>
                        {/* Ajuste é o saldo contado, não um delta: "+7" diria que entraram 7. */}
                        <span className={`text-sm font-bold ${m.type === 'out' ? 'text-red-600 dark:text-red-400' : m.type === 'adjustment' ? 'text-foreground' : 'text-emerald-600 dark:text-emerald-400'}`}>
                          {m.type === 'out' ? '-' : m.type === 'adjustment' ? '= ' : '+'}{formatStockNumber(m.quantity)}
                        </span>
                      </div>
                      {m.notes && <p className="text-xs text-muted-foreground truncate">{m.notes}</p>}
                      <p className="text-[10px] text-muted-foreground/70">
                        {new Date(m.createdAt).toLocaleDateString('pt-BR')} às {new Date(m.createdAt).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )
          )}

          {tab === 'purchases' && (
            purchasesQuery.isLoading ? (
              <div className="flex justify-center py-8">
                <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
              </div>
            ) : purchasesQuery.isError && (purchasesQuery.data?.purchases ?? []).length === 0 ? (
              <ErrorState
                title="Não foi possível carregar as compras"
                error={purchasesQuery.error}
                onRetry={() => purchasesQuery.refetch()}
              />
            ) : (purchasesQuery.data?.purchases ?? []).length === 0 ? (
              <EmptyState
                compact
                icon={<ShoppingCart size={22} />}
                title="Nenhuma compra registrada"
                description="Quando este item entrar por uma ordem de compra, o histórico de preço aparece aqui."
              />
            ) : (
              <div className="space-y-2">
                {(purchasesQuery.data?.purchases ?? []).map((p) => (
                  <div key={`${p.poId}-${p.createdAt}`} className="rounded-lg border px-3 py-2.5 space-y-1.5">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">OC #{p.poNumber}</span>
                      <POStatusBadge status={p.poStatus as 'draft'} />
                    </div>
                    <p className="text-xs text-muted-foreground">{p.supplierName}</p>
                    <div className="grid grid-cols-3 gap-2 text-xs">
                      <div>
                        <span className="text-muted-foreground">Pedido: </span>
                        <span className="font-medium">{formatStockNumber(p.quantityOrdered)}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Recebido: </span>
                        <span className="font-medium">{formatStockNumber(p.quantityReceived)}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Preço: </span>
                        <span className="font-medium">{p.unitPrice == null ? '—' : formatReais(p.unitPrice)}</span>
                      </div>
                    </div>
                    <p className="text-[10px] text-muted-foreground/70">
                      {new Date(p.createdAt).toLocaleDateString('pt-BR')}
                    </p>
                  </div>
                ))}
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
}

function InfoField({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-sm font-medium">{value}</div>
    </div>
  );
}
